import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  TableSortLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Tooltip,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { NotificationManager } from "react-notifications";
import CommonLoader from "../../common/CommonLoader";
import ViewQueueItem from "./ViewQueueItem";
import {
  GetEtkRegistrationQueueOrders,
  UpdateEtkRegistrationQueueOrder,
  DeleteEtkRegistrationQueueOrder,
} from "../../../services/EtkRegistrationService";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import {
  getComparator,
  stableSort,
} from "../../../../common-utilities/sortingUtils";
import { formatDateTime } from "../../../../common-utilities/date-utility";

const EtkRegistrationQueueTable = () => {
  const columns = [
    "ID",
    "Order Number",
    "Approval Status",
    "RegisteredAt",
    "Kit Type",
    "Client Name",
    "Need Rush",
    "Actions",
  ];
  const columnHeaders = [
    "id",
    "orderNumber",
    "isKitApproved",
    "registeredAt",
    "vlpName",
    "clientName",
    "needRush",
  ];
  const tableContainerRef = useRef(null);
  const [tableHeight, setTableHeight] = useState(460);
  const rowsPerPageOptions = [20, 50, 100, 150, 200, 400, 600, 800, 1000];

  const [etkRegistrationQueueData, setEtkRegistrationQueueData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [loading, setLoading] = useState(false);
  const [isItemView, setIsItemView] = useState(false);
  const [isChangeSuccessful, setIsChangeSuccessful] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [editedOrderNumber, setEditedOrderNumber] = useState("");
  const [originalOrderNumber, setOriginalOrderNumber] = useState("");
  const [searchCriteria, setSearchCriteria] = useState({
    searchText: "",
    orderStatus: "",
    startDate: "",
    endDate: "",
  });
  const [orderBy, setOrderBy] = useState("registeredAt");
  const [order, setOrder] = useState("desc");

  const getDefaultDateRange = () => {
    const today = new Date();

    const startDateValue = new Date(today);
    startDateValue.setMonth(today.getMonth() - 1);

    return {
      startDate: startDateValue.toISOString().split("T")[0],
      endDate: today.toISOString().split("T")[0],
    };
  };

  const hasLoadedRef = useRef(false);

  // Also fires once on mount (before hasLoadedRef is set), so mount and the
  // post-mutation refresh (from approve/delete/edit) share one fetch path
  // instead of racing two separate effects with different criteria.
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      const initialCriteria = {
        searchText: "",
        orderStatus: "",
        ...getDefaultDateRange(),
      };
      setSearchCriteria(initialCriteria);
      getEtkOrders(initialCriteria);
    } else {
      getEtkOrders(searchCriteria);
    }
  }, [isChangeSuccessful]);

  useEffect(() => {
    const calculateTableHeight = () => {
      if (tableContainerRef.current) {
        const containerTop = tableContainerRef.current.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        const availableHeight = windowHeight - containerTop - 76;
        const calculatedHeight = Math.max(300, Math.min(availableHeight, windowHeight - 200));
        setTableHeight(calculatedHeight);
      }
    };

    calculateTableHeight();
    window.addEventListener('resize', calculateTableHeight);
    const timeoutId = setTimeout(calculateTableHeight, 100);

    return () => {
      window.removeEventListener('resize', calculateTableHeight);
      clearTimeout(timeoutId);
    };
  }, [etkRegistrationQueueData]); // re-runs when data changes


  const getEtkOrders = async (criteria) => {
    try {
      setLoading(true);
      let response = await GetEtkRegistrationQueueOrders(
        criteria.searchText,
        criteria.orderStatus,
        criteria.startDate,
        criteria.endDate
      );
      setEtkRegistrationQueueData(response.results || []);
    } catch (err) {
      NotificationManager.error("Error getting Etk Registration Queue Orders.");
    } finally {
      setLoading(false);
    }
  };

  const updateEtkOrder = async () => {
    try {
      setLoading(true);

      const payload = {
        ...selectedItem,
        needRush:
          selectedItem.needRush === true ||
          selectedItem.needRush === "Yes",
      };

      let response = await UpdateEtkRegistrationQueueOrder(
        editedOrderNumber,
        payload
      );

      if (response.results) {
        NotificationManager.success("Order number updated successfully.");
        setIsChangeSuccessful(!isChangeSuccessful);
        setEditDialogOpen(false);
      } else {
        NotificationManager.error("Error updating order number.");
      }
    } catch (err) {
      NotificationManager.error("Error updating order number.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirmationClose = () => {
    setDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      setLoading(true);
      let response = await DeleteEtkRegistrationQueueOrder(
        selectedItem.id
      );
      if (response.results) {
        NotificationManager.success("Item deleted successfully.");
        setIsChangeSuccessful(!isChangeSuccessful);
      } else {
        NotificationManager.error("Error deleting item.");
      }
    } catch (err) {
      NotificationManager.error("Error deleting item.");
    } finally {
      setLoading(false);
    }
    setDeleteConfirmationOpen(false);
  };

  const handleBackButtonClick = () => {
    setIsItemView(false);
    setIsChangeSuccessful(!isChangeSuccessful);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewClick = (etkRegistrationItem) => {
    setSelectedItem(etkRegistrationItem);
    setIsItemView(true);
  };

  const handleEditClick = (etkRegistrationItem) => {
    setSelectedItem(etkRegistrationItem);
    setOriginalOrderNumber(etkRegistrationItem.orderNumber);
    setEditedOrderNumber(etkRegistrationItem.orderNumber);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (etkRegistrationItem) => {
    setSelectedItem(etkRegistrationItem);
    setDeleteConfirmationOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setOriginalOrderNumber("");
    setEditedOrderNumber("");
    setSelectedItem(null);
  };

  const handleSaveEdit = async () => {
    updateEtkOrder();
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Approved + deleted means the order was synced to a lab order.
  const isSynced = (item) => Boolean(item.isKitApproved && item.isDeleted);

  const renderActions = (item) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditClick(item)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(item)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(item)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  const getIsKitPaid = (etkRegistrationItem) => {
    return etkRegistrationItem.preModelJson && !etkRegistrationItem.modelJson
      ? "Paid"
      : "Not Paid";
  };

  const getNeedRush = (etkRegistrationItem) => {
    return etkRegistrationItem.needRush ? "Yes" : "No";
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(0);
    getEtkOrders(searchCriteria);
  };

  const handleApproveClick = () => {
    setIsItemView(false);
    setIsChangeSuccessful(!isChangeSuccessful);
  };

  // Prepare data for sorting
  const prepareDataForSorting = (data) => {
    return data.map((item) => ({
      ...item,
      vlpName: item.kitJson?.vlpName || "",
      needRush: getNeedRush(item),
    }));
  };

  const sortedData = stableSort(
    prepareDataForSorting(etkRegistrationQueueData),
    getComparator(order, orderBy)
  );

  return (
    <>
      <span className="title">ETK Registration Queue </span>
      <hr />
      <Paper elevation={3} sx={{ padding: 3 }}>
        {isItemView ? (
          <>
            <IconButton
              onClick={handleBackButtonClick}
              sx={{ mb: 2 }}
              style={{ color: "green" }}
            >
              <ArrowBackIcon />
            </IconButton>
            {selectedItem !== null && (
              <ViewQueueItem
                queueItem={selectedItem}
                onApprove={handleApproveClick}
              />
            )}
          </>
        ) : (
          <>
            <form onSubmit={handleSearchSubmit}>
              <Grid container spacing={2} alignItems="center" sx={{ marginBottom: "16px" }}>
                <Grid item xs={12} sm={3}>
                  <Tooltip
                    title="Search by ID, order number, client name, or kit type"
                    arrow
                    placement="top"
                  >
                    <TextField
                      label="Search Criteria"
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={searchCriteria.searchText}
                      onChange={(e) =>
                        setSearchCriteria({ ...searchCriteria, searchText: e.target.value })
                      }
                    />
                  </Tooltip>
                </Grid>

                <Grid item xs={12} sm={2.5}>
                  <FormControl variant="outlined" fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={searchCriteria.orderStatus}
                      onChange={(e) =>
                        setSearchCriteria({ ...searchCriteria, orderStatus: e.target.value })
                      }
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="Approved">Approved</MenuItem>
                      <MenuItem value="Not Approved">Not Approved</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={2}>
                  <Tooltip title="Filter by date registered" arrow placement="top">
                    <TextField
                      label="From Date"
                      type="date"
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={searchCriteria.startDate}
                      onChange={(e) =>
                        setSearchCriteria({ ...searchCriteria, startDate: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Tooltip>
                </Grid>

                <Grid item xs={12} sm={2}>
                  <Tooltip title="Filter by date registered" arrow placement="top">
                    <TextField
                      label="To Date"
                      type="date"
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={searchCriteria.endDate}
                      onChange={(e) =>
                        setSearchCriteria({ ...searchCriteria, endDate: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Tooltip>
                </Grid>

                <Grid item xs={12} sm={2.5}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="medium"
                    style={{ height: "40px" }}
                  >
                    Search
                  </Button>
                </Grid>
              </Grid>
            </form>

            <TableContainer ref={tableContainerRef} sx={{ maxHeight: tableHeight, overflowY: "auto", width: "99%" }}>
              <Table sx={{ minWidth: 400 }} size="small" dense stickyHeader>
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((column, index) => (
                      <TableCell
                        key={column}
                        onClick={() => handleRequestSort(columnHeaders[index])}
                        style={{ cursor: columnHeaders[index] !== "Actions" ? "pointer" : "default" }}
                      >
                        {columnHeaders[index] !== undefined ? (
                          <TableSortLabel
                            active={orderBy === columnHeaders[index]}
                            direction={
                              orderBy === columnHeaders[index] ? order : "asc"
                            }
                          >
                            {column}
                          </TableSortLabel>
                        ) : (
                          column
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item, index) => (
                      <TableRow
                        key={item.id}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                        }}
                      >
                        <TableCell>{item.id}</TableCell>
                        <TableCell>
                          <span style={{ color: "primary" }}>
                            {item.orderNumber}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span
                            style={{
                              color: item.isKitApproved ? "green" : "red",
                            }}
                          >
                            {item.isKitApproved ? "Approved" : "Not Approved"}
                          </span>
                          {/* Approved + deleted means the order was synced to a lab order */}
                          {isSynced(item) && (
                            <Chip
                              label="Synced"
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>{formatDateTime(item.registeredAt)}</TableCell>
                        <TableCell>{item.vlpName}</TableCell>
                        <TableCell>{item.clientName}</TableCell>
                        <TableCell>
                          <span
                            style={{
                              color:
                                item.needRush !== "Yes" ? "green" : "red",
                            }}
                          >
                            {item.needRush}
                          </span>
                        </TableCell>
                        <TableCell>{renderActions(item)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={etkRegistrationQueueData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <CommonLoader loading={loading} />
            <Dialog open={editDialogOpen} onClose={handleEditDialogClose}>
              <DialogTitle>
                Edit Order Number : {originalOrderNumber}
              </DialogTitle>
              <DialogContent>
                <TextField
                  label="New Order Number"
                  value={editedOrderNumber}
                  style={{ marginTop: "10px" }}
                  onChange={(e) => setEditedOrderNumber(e.target.value)}
                  fullWidth
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleEditDialogClose}>Cancel</Button>
                <Button onClick={handleSaveEdit} color="primary" variant="contained">
                  Save
                </Button>
              </DialogActions>
            </Dialog>
            <DeleteConfirmation
              isOpen={deleteConfirmationOpen}
              onClose={handleDeleteConfirmationClose}
              onConfirm={handleDeleteConfirmationConfirm}
              title="Delete Confirmation"
              message={`Are you sure you want to delete the item?`}
              color="red"
            />
          </>
        )}
      </Paper>
    </>
  );
};

export default EtkRegistrationQueueTable;